"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_native_1 = require("react-native");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const pro_light_svg_icons_1 = require("@fortawesome/pro-light-svg-icons");
const colors_1 = require("../../common/colors");
const useDevice_1 = require("../../hooks/useDevice");
const AppText_1 = require("../AppText");
const ScrollCustomIndicator_1 = __importDefault(require("../ScrollCustomIndicator"));
const document_1 = require("../../services/document");
const document_2 = require("../../helper/document");
const lottie_react_native_1 = __importDefault(require("lottie-react-native"));
const Search = ({ navigation }) => {
    const { isMobileScreen } = (0, useDevice_1.useDevice)();
    const [searchQuery, setSearchQuery] = (0, react_1.useState)('');
    const [searchResults, setSearchResults] = (0, react_1.useState)([]);
    const [isResultHovered, setIsResultHovered] = (0, react_1.useState)(null);
    const [isUrlInput, setIsUrlInput] = (0, react_1.useState)(false);
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [hasSearched, setHasSearched] = (0, react_1.useState)(false);
    const handleSearch = (0, react_1.useCallback)(async () => {
        if (searchQuery.length > 0) {
            // Check if input looks like a URL
            const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
            setIsUrlInput(urlPattern.test(searchQuery));
            setIsLoading(true);
            setHasSearched(true);
            try {
                const results = await (0, document_1.searchDocuments)(searchQuery);
                if (Array.isArray(results)) {
                    setSearchResults(results);
                }
                else {
                    setSearchResults([]);
                }
            }
            finally {
                setIsLoading(false);
            }
        }
        else {
            setSearchResults([]);
            setIsUrlInput(false);
            setHasSearched(false);
        }
    }, [searchQuery]);
    const handleKeyPress = (0, react_1.useCallback)((e) => {
        if (e.nativeEvent.key === 'Enter') {
            handleSearch();
        }
    }, [handleSearch]);
    const handleClickOnResult = (0, react_1.useCallback)((conversationId) => {
        navigation.navigate('Summary', { conversationId });
    }, [navigation]);
    return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: [
            styles.container,
            { height: '100%', marginTop: 72 }
        ], children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [
                styles.content,
                isMobileScreen && styles.contentMobile
            ], children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.searchContainer, children: (0, jsx_runtime_1.jsx)(react_native_1.TextInput, { placeholder: "Try searching in natural language, e.g. 'notes about Haiti from last week'", placeholderTextColor: colors_1.Colors.DARK_200, value: searchQuery, onChangeText: setSearchQuery, onKeyPress: handleKeyPress, style: [
                            styles.searchInput,
                            isUrlInput && styles.searchInputUrl
                        ] }) }), hasSearched && ((0, jsx_runtime_1.jsx)(ScrollCustomIndicator_1.default, { style: styles.results, children: isLoading ? ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.loaderContainer, children: (0, jsx_runtime_1.jsx)(lottie_react_native_1.default, { source: require("../../assets/icons/lottie/spinner.json"), autoPlay: true, loop: true, style: styles.loader }) })) : ((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: searchResults.length > 0 ? (searchResults.map((result) => ((0, jsx_runtime_1.jsxs)(react_native_1.Pressable, { onPress: () => { handleClickOnResult(result.conversationId); }, onHoverIn: () => setIsResultHovered(result.conversationId), onHoverOut: () => setIsResultHovered(null), style: [
                                styles.resultItem,
                                isResultHovered === result.conversationId && styles.resultItemHovered
                            ], children: [(0, jsx_runtime_1.jsx)(react_native_1.Image, { source: { uri: result.images[result.images.length - 1] || '' }, style: { width: 64, height: 64, borderRadius: 8 } }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.resultContent, children: [(0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.resultTitle, children: result.title }), (0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.resultDescription, numberOfLines: 2, children: (0, document_2.getMarkdownContent)(result.summaryResponse, 'Summary') })] }), (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: pro_light_svg_icons_1.faChevronRight, color: isResultHovered === result.conversationId ? colors_1.Colors.white : colors_1.Colors.DARK_200, size: 16 })] }, result.conversationId)))) : ((0, jsx_runtime_1.jsx)(AppText_1.AppText, { style: styles.resultCount, children: "No matching results were found" })) })) }))] }) }));
};
const styles = react_native_1.StyleSheet.create({
    container: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    containerHidden: {
        display: 'none',
    },
    backdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors_1.Colors.DARK_700,
        opacity: 0.95,
        width: '100%',
    },
    content: {
        backgroundColor: colors_1.Colors.DARK_700,
        padding: 24,
        zIndex: 50,
        width: '100%',
        maxWidth: 850,
        position: 'relative',
        borderRadius: 8,
        marginHorizontal: 'auto',
        height: '100%'
    },
    contentMobile: {
        padding: 16,
        maxWidth: '100%',
        height: '100%',
        borderRadius: 0,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    title: {
        fontSize: 24,
        fontWeight: '600',
        color: colors_1.Colors.white,
    },
    closeButton: {
        padding: 8,
        borderRadius: 20,
    },
    closeButtonHovered: {
        backgroundColor: colors_1.Colors.DARK_600,
    },
    searchContainer: {
        marginBottom: 16,
    },
    searchInput: {
        width: '100%',
        height: 48,
        backgroundColor: colors_1.Colors.DARK_600,
        borderRadius: 8,
        paddingHorizontal: 16,
        color: colors_1.Colors.white,
    },
    searchInputUrl: {
        borderColor: colors_1.Colors.PRIMARY_BLUE,
        borderWidth: 1,
    },
    results: {
        marginTop: 16,
    },
    loaderContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 32,
    },
    loader: {
        width: 50,
        height: 50,
    },
    resultCount: {
        fontSize: 14,
        color: colors_1.Colors.DARK_200,
        marginBottom: 8,
    },
    resultItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        borderRadius: 8,
    },
    resultItemHovered: {
        backgroundColor: colors_1.Colors.DARK_600,
    },
    resultImage: {
        width: 64,
        height: 64,
        borderRadius: 8,
    },
    resultContent: {
        flex: 1,
    },
    resultTitle: {
        fontSize: 16,
        fontWeight: '500',
        color: colors_1.Colors.white,
        marginBottom: 4,
    },
    resultDescription: {
        fontSize: 14,
        color: colors_1.Colors.DARK_200,
    },
});
exports.default = Search;
